import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { VFC } from "react";
import styled from "styled-components";
import { LegacyImage, Markdown, SupTitle } from "components";
import { GeneralRoutingLinkButton } from "components/Molecules/GeneralRoutingLinkButton";
import { FullArrow } from "icons";
import { StarBurstFull } from "icons/StarBurstFull";
import { StarBurstHalf } from "icons/StarBurstHalf";
import { Hero4Content } from "types/graphql/generated";

interface Props {
  content: Hero4Content;
}

const Hero4: VFC<Props> = withErrorBoundary(
  ({
    content: {
      hero4SupTitle,
      hero4Title,
      hero4Description,
      hero4ButtonLabel,
      hero4ButtonLink,
      hero4Person1Image: {
        data: { hero4Person1Image },
      },
      hero4Person2Image: {
        data: { hero4Person2Image },
      },
      person1MobileImage: {
        data: { person1MobileImage },
      },
    },
  }) => {
    return (
      <StyledHero4>
        <div className="Hero4__container Content">
          <div className="Hero4__textSection">
            {!!hero4SupTitle && <SupTitle color="Color_secondary">{hero4SupTitle}</SupTitle>}
            <h1 className="Hero4__title Font_80_88_600 sm:Font_64_72_600">{hero4Title}</h1>
            <Markdown className="Hero4__description Color_secondary Font_24_32_450">{hero4Description}</Markdown>
            <GeneralRoutingLinkButton href={hero4ButtonLink} className="Hero4__button" rightIcon={<FullArrow />}>
              {hero4ButtonLabel}
            </GeneralRoutingLinkButton>
          </div>
          <div className="Hero4__imageSection">
            <div className="Hero4__image1Container">
              <LegacyImage
                {...hero4Person1Image}
                layout="fill"
                objectFit="cover"
                objectPosition="center"
                sizes="40vw"
                className="Hero4__imageDesktop"
              />
              <StarBurstFull className="Hero4__starBurstFull" />
              <StarBurstHalf className="Hero4__starBurstHalf" />
            </div>
            <LegacyImage
              {...hero4Person2Image}
              layout="fill"
              objectFit="contain"
              objectPosition="left"
              sizes="40vw"
              className="Hero4__image2"
            />
          </div>
          <LegacyImage {...person1MobileImage} layout="responsive" sizes="100vw" className="Hero4__imageMobile" />
        </div>
      </StyledHero4>
    );
  },
  {}
);

const StyledHero4 = styled.section`
  background-color: ${({ theme }) => theme.colors.coral["05"]};
  padding-top: 80px;
  padding-bottom: 96px;

  .Hero4__container {
    position: relative;
  }

  .Hero4__textSection {
    position: relative;
    z-index: 1;
  }

  .Hero4__title {
    max-width: 631px;
    margin-top: 32px;
  }

  .Hero4__description {
    max-width: 384px;
    margin-top: 24px;
  }

  .Hero4__button {
    width: fit-content;
    padding: 19px 32px;
    margin-top: 56px;
  }

  .Hero4__imageSection {
    position: absolute;
    width: 100vw;
    height: 626px;
    top: -40px;
    left: 40.8%;
    display: flex;
  }

  .Hero4__image1Container {
    position: relative;
    width: 530px;
    height: 570px;
  }

  .Hero4__imageDesktop {
    position: relative;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
  }

  .Hero4__starBurstFull {
    position: absolute;
    left: -109px;
    bottom: -56px;
  }

  .Hero4__starBurstHalf {
    position: absolute;
    top: 0;
    left: calc(50% + 38px);
  }

  .Hero4__image2 {
    position: absolute;
    width: 100vw;
    height: 553px;
    left: calc(530px + 32px);
    top: 73px;
  }

  .Hero4__imageMobile {
    display: none;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.xl}px`}) {
    padding-top: 95px;
    padding-bottom: 80px;

    .Hero4__title {
      max-width: 372px;
    }

    .Hero4__imageSection {
      top: -25px;
      left: 45.5%;
    }

    .Hero4__image1Container {
      max-width: 54.5vw;
    }

    .Hero4__imageDesktop {
      max-width: 100%;
    }

    .Hero4__starBurstFull {
      left: -109px;
      bottom: -82px;
    }

    .Hero4__starBurstHalf {
      top: 0;
      right: -63px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    padding-top: 40px;
    padding-bottom: 0;
    margin-bottom: -60px;
    background-color: inherit;

    .Hero4__container {
      flex-direction: column;
    }

    .Hero4__title {
      max-width: 100%;
      margin-top: 16px;
    }

    .Hero4__description {
      max-width: 100%;
      margin-top: 16px;
    }

    .Hero4__button {
      margin-top: 32px;
      padding: 15px 24px;
    }

    .Hero4__imageSection {
      display: none;
    }

    .Hero4__starBurstFull,
    .Hero4__starBurstHalf {
      display: none;
    }

    .Hero4__imageMobile {
      display: block;
      margin-top: 64px;
      line-height: 0;
    }
  }
`;

gql`
  fragment Hero4Content on ComponentHeroHero4 {
    hero4SupTitle
    hero4Title
    hero4Description
    hero4ButtonLabel
    hero4ButtonLink
    hero4Person1Image {
      data {
        hero4Person1Image: attributes {
          src: hash
          alt: alternativeText
        }
      }
    }
    hero4Person2Image {
      data {
        hero4Person2Image: attributes {
          src: hash
          alt: alternativeText
        }
      }
    }
    person1MobileImage {
      data {
        person1MobileImage: attributes {
          src: hash
          alt: alternativeText
          width: width
          height: height
        }
      }
    }
  }
`;

export { Hero4 };
export type { Props as Hero4Props };
